import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, roundedPoly, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

const OBSIDIAN = 0x3a1f22; // charred shell
const MAGMA = 0xff7a30;

/**
 * B6 Pyre Core — fire Elemental. A cracked obsidian
 * shell barely containing a molten heart; flame crown grows when enraged.
 * Frame 0 calm / 1 enraged. docs/02 Elementals.
 */
export const bossPyreBody: ProceduralDef = {
  key: 'boss/pyre/body',
  width: 130,
  height: 130,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const enraged = frame === 1;

    // Outer heat shimmer
    const halo = ctx.createRadialGradient(c, c, 28, c, c, c);
    halo.addColorStop(0, enraged ? 'rgba(255, 122, 48, 0.5)' : 'rgba(255, 122, 48, 0.3)');
    halo.addColorStop(1, 'rgba(255, 122, 48, 0)');
    circle(ctx, c, c, c);
    ctx.fillStyle = halo;
    ctx.fill();

    // Flame crown — taller tongues when enraged
    ctx.fillStyle = css(enraged ? COLORS.dangerRed : MAGMA);
    const tongues = 7;
    for (let i = 0; i < tongues; i++) {
      const a = (Math.PI * 2 * i) / tongues - Math.PI / 2;
      const len = (enraged ? 30 : 20) + (i % 2) * 8;
      const bx = c + Math.cos(a) * 38;
      const by = c + Math.sin(a) * 38;
      ctx.beginPath();
      ctx.moveTo(bx + Math.cos(a + 1.7) * 9, by + Math.sin(a + 1.7) * 9);
      ctx.quadraticCurveTo(
        c + Math.cos(a + 0.12) * (38 + len * 1.2),
        c + Math.sin(a + 0.12) * (38 + len * 1.2),
        bx + Math.cos(a - 1.7) * 9, by + Math.sin(a - 1.7) * 9,
      );
      ctx.fill();
    }

    // Obsidian shell — faceted sphere
    roundedPoly(
      ctx,
      [
        [c, c - 40], [c + 30, c - 26], [c + 40, c + 4], [c + 22, c + 34],
        [c - 22, c + 34], [c - 40, c + 4], [c - 30, c - 26],
      ],
      10,
    );
    fillOutlined(ctx, OBSIDIAN, 6.5);

    // Molten cracks leaking light
    ctx.strokeStyle = css(enraged ? COLORS.dangerRed : MAGMA);
    ctx.lineWidth = enraged ? 5 : 4;
    ctx.lineCap = 'round';
    for (const path of [
      [[c - 24, c - 12], [c - 8, c - 2], [c - 14, c + 18]],
      [[c + 6, c - 30], [c + 12, c - 8], [c + 28, c + 2]],
      [[c - 2, c + 8], [c + 6, c + 22]],
    ] as const) {
      ctx.beginPath();
      ctx.moveTo(path[0]![0], path[0]![1]);
      for (let i = 1; i < path.length; i++) ctx.lineTo(path[i]![0], path[i]![1]);
      ctx.stroke();
    }

    // Molten heart
    circle(ctx, c, c, enraged ? 15 : 12);
    fillOutlined(ctx, MAGMA, 3.5);
    circle(ctx, c - 3, c - 4, 4);
    ctx.fillStyle = css(shade(MAGMA, 1.5));
    ctx.fill();
    void h;
  },
};
